import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { LanguageProvider, useLang } from './context/LanguageContext';
import LandingPage from './pages/LandingPage';
import BookingPage from './pages/BookingPage';
import ClientPage from './pages/ClientPage';
import DriverPage from './pages/DriverPage';
import RidersPage from './pages/RidersPage';
import DriversPage from './pages/DriversPage';
import AboutPage from './pages/AboutPage';
import HelpPage from './pages/HelpPage';
import AuthForm from './components/AuthForm';
import ProfilePage from './pages/ProfilePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AdminPage from './pages/AdminPage';
import Footer from './components/Footer';
import NotificationBell from './components/NotificationBell';
import { RideEventsProvider } from './context/RideEventsContext';
import { createRide } from './services/api';

const LANGS = ['en', 'ru', 'hy', 'fr', 'de'];
const LANG_LABELS = { en: 'English', ru: 'Русский', hy: 'Հայերեն', fr: 'Français', de: 'Deutsch' };

function loadUser() {
  try { return JSON.parse(localStorage.getItem('taxi-user')); } catch { return null; }
}

function AppInner() {
  const [user, setUser]               = useState(loadUser);
  const [authRole, setAuthRole]       = useState('CLIENT');
  const [menuOpen, setMenuOpen]       = useState(false);
  const [langOpen, setLangOpen]       = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    if (!langOpen) return;
    const close = () => setLangOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [langOpen]);

  useEffect(() => {
    const handleInvalid = (e) => {
      if (e.target.validity && e.target.validity.valueMissing) {
        e.target.setCustomValidity(t.validation?.required || 'This field is required.');
      } else if (e.target.validity && e.target.validity.typeMismatch) {
        e.target.setCustomValidity(t.validation?.email || 'Please enter a valid email address.');
      }
    };
    const clearCustomValidity = (e) => {
      if (e.target.setCustomValidity) e.target.setCustomValidity('');
    };
    document.addEventListener('invalid', handleInvalid, true);
    document.addEventListener('input', clearCustomValidity, true);
    return () => {
      document.removeEventListener('invalid', handleInvalid, true);
      document.removeEventListener('input', clearCustomValidity, true);
    };
  }, [t]);

  const go = (path) => { navigate(path); setMenuOpen(false); };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    if (userData.role === 'ADMIN') { navigate('/admin'); return; }
    if (userData.role === 'DRIVER') { navigate('/driver'); return; }
    if (pendingBooking) {
      setPendingBooking(null);
      createRide(pendingBooking).catch(() => toast.error(t.client.bookError));
    }
    navigate('/client');
  };

  const handleLogout = () => {
    localStorage.removeItem('taxi-token');
    localStorage.removeItem('taxi-user');
    setUser(null);
    navigate('/');
  };

  const goClient = () => navigate(user ? '/client' : '/booking');
  const goDriver = () => {
    if (user) { navigate('/driver'); return; }
    setAuthRole('DRIVER');
    navigate('/auth');
  };

  return (
    <RideEventsProvider user={user}>
    <div className="min-h-screen flex flex-col">
      <Toaster position="top-right" toastOptions={{ duration: 4000, style: { borderRadius: '12px', fontSize: '14px' } }} />

      {/* ── Navbar ── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => go('/')} className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center shadow-sm group-hover:bg-primary-700 transition-colors">
              <span className="text-white text-sm font-black">H</span>
            </div>
            <span className="text-slate-900 font-black text-lg tracking-tight">
              Hay<span className="text-primary-600">Taxi</span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-2">
            <button onClick={() => go('/riders')}  className={`text-sm font-medium transition-colors border-2 px-5 py-2 rounded-full ${pathname === '/riders'  ? 'border-primary-500 text-primary-600 bg-primary-50' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>{t.nav.riders}</button>
            <button onClick={() => go('/drivers')} className={`text-sm font-medium transition-colors border-2 px-5 py-2 rounded-full ${pathname === '/drivers' ? 'border-primary-500 text-primary-600 bg-primary-50' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>{t.nav.drivers}</button>
            <button onClick={() => go('/about')}   className={`text-sm font-medium transition-colors border-2 px-5 py-2 rounded-full ${pathname === '/about'   ? 'border-primary-500 text-primary-600 bg-primary-50' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>{t.nav.about}</button>
          </nav>

          <div className="flex items-center gap-1">
            <div className="relative mr-2">
              <button
                onClick={(e) => { e.stopPropagation(); setLangOpen((o) => !o); }}
                className="flex items-center gap-1.5 border border-slate-200 rounded-full px-3 py-1.5 text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253" />
                </svg>
                <span className="text-xs font-bold uppercase">{lang}</span>
                <svg className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-slate-200 rounded-2xl shadow-lg py-1 z-50">
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      onClick={() => { setLang(l); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${lang === l ? 'font-bold text-primary-600 bg-primary-50' : 'text-slate-700 hover:bg-slate-50'}`}
                    >
                      {LANG_LABELS[l]}
                      {lang === l && <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => go('/help')} className={`hidden md:block text-sm font-semibold px-5 py-2 rounded-full border-2 transition-colors ${pathname === '/help' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}>
              {t.nav.help}
            </button>

            {user && <NotificationBell />}

            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <button onClick={() => go('/profile')} className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors">{user.name}</button>
                <button onClick={handleLogout} className="text-sm font-semibold px-5 py-2 rounded-full border-2 border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors">
                  {t.nav.signOut}
                </button>
              </div>
            ) : (
              <button onClick={() => { setAuthRole('CLIENT'); go('/auth'); }} className="hidden md:block text-sm font-semibold px-5 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                {t.nav.signIn}
              </button>
            )}

            {/* Гамбургер */}
            <button onClick={() => setMenuOpen((o) => !o)} className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Menu">
              {menuOpen ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Мобильное меню */}
        {menuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white px-6 py-4 space-y-1">
            <button onClick={() => go('/riders')}  className={`w-full text-left text-sm font-medium px-4 py-3 rounded-xl transition-colors ${pathname === '/riders'  ? 'bg-primary-50 text-primary-600 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}>{t.nav.riders}</button>
            <button onClick={() => go('/drivers')} className={`w-full text-left text-sm font-medium px-4 py-3 rounded-xl transition-colors ${pathname === '/drivers' ? 'bg-primary-50 text-primary-600 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}>{t.nav.drivers}</button>
            <button onClick={() => go('/about')}   className={`w-full text-left text-sm font-medium px-4 py-3 rounded-xl transition-colors ${pathname === '/about'   ? 'bg-primary-50 text-primary-600 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}>{t.nav.about}</button>
            <button onClick={() => go('/help')}    className={`w-full text-left text-sm font-medium px-4 py-3 rounded-xl transition-colors ${pathname === '/help'    ? 'bg-primary-50 text-primary-600 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}>{t.nav.help}</button>
            <div className="pt-2 border-t border-slate-100">
              {user ? (
                <div className="space-y-1">
                  <button onClick={() => go('/profile')} className={`w-full text-left text-sm font-medium px-4 py-3 rounded-xl transition-colors ${pathname === '/profile' ? 'bg-primary-50 text-primary-600 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}>
                    {user.name}
                  </button>
                  <button onClick={handleLogout} className="w-full text-left text-sm font-semibold px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition-colors">
                    {t.nav.signOut}
                  </button>
                </div>
              ) : (
                <button onClick={() => { setAuthRole('CLIENT'); go('/auth'); }} className="w-full text-sm font-semibold px-4 py-3 rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                  {t.nav.signIn}
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* ── Routes ── */}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage onBook={goClient} onDrive={goDriver} />} />
          <Route path="/booking" element={
            <BookingPage
              user={user}
              onBack={() => navigate('/')}
              onConfirm={async (booking) => { await createRide(booking); navigate('/client'); }}
              onAuthNeeded={(booking) => { setPendingBooking(booking); setAuthRole('CLIENT'); navigate('/auth'); }}
            />
          } />
          <Route path="/auth" element={
            <AuthForm
              onBack={() => navigate(pendingBooking ? '/booking' : '/')}
              onSuccess={handleAuthSuccess}
              defaultRole={authRole}
            />
          } />
          <Route path="/client"  element={user ? <ClientPage  user={user} onBack={() => navigate('/')} /> : <Navigate to="/auth" />} />
          <Route path="/driver"  element={user ? <DriverPage  user={user} onBack={() => navigate('/')} /> : <Navigate to="/auth" />} />
          <Route path="/profile" element={user ? <ProfilePage user={user} onBack={() => navigate(user.role === 'DRIVER' ? '/driver' : user.role === 'ADMIN' ? '/admin' : '/client')} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/admin"   element={user?.role === 'ADMIN' ? <AdminPage onBack={() => navigate('/')} /> : <Navigate to="/" />} />
          <Route path="/riders"  element={<RidersPage  onBook={goClient} />} />
          <Route path="/drivers" element={<DriversPage onDrive={goDriver} />} />
          <Route path="/about"   element={<AboutPage   onBook={goClient} onDrive={goDriver} />} />
          <Route path="/help"    element={<HelpPage    onBack={() => navigate('/')} />} />
          <Route path="/reset-password" element={<ResetPasswordPage onDone={() => { navigate('/auth'); }} />} />
          <Route path="/verify-email"   element={<VerifyEmailPage   onDone={() => { navigate('/auth'); }} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>

      <Footer onNavigate={(p) => go(`/${p === 'home' ? '' : p}`)} />
    </div>
    </RideEventsProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AppInner />
      </LanguageProvider>
    </BrowserRouter>
  );
}
